import React, { useEffect, useRef, useState, useMemo } from 'react';
import '../Style/Impact.css';
import { useLanguage } from '../Context/Languagecontext';

// Structural design config array kept static outside rendering scope
const STATS_STRUCTURE = [
  {
    id: 'experience',
    value: 25,
    suffix: '+',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M20 10 L20 20 L28 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="20" cy="20" r="2" fill="currentColor"/>
      </svg>
    ),
  },
  {
    id: 'businesses',
    value: 120,
    suffix: '+',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="18" width="8" height="14" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="16" y="12" width="8" height="20" stroke="currentColor" strokeWidth="1.2"/>
        <rect x="26" y="8" width="8" height="24" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M4 32 L36 32" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'workforce',
    value: 5000,
    suffix: '+',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="14" cy="13" r="5" stroke="currentColor" strokeWidth="1.2"/>
        <circle cx="26" cy="13" r="5" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M4 32 C4 24 10 20 14 20 C18 20 22 22 24 24" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M26 22 C30 22 36 26 36 32" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'customers',
    value: 50000,
    suffix: '+',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6 L24 14 L34 15.5 L27 22 L28.5 32 L20 28 L11.5 32 L13 22 L6 15.5 L16 14 Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'reach',
    value: 8,
    suffix: '+',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.2"/>
        <ellipse cx="20" cy="20" rx="6" ry="14" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M6 20 L34 20" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M8 13 Q20 17 32 13" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
        <path d="M8 27 Q20 23 32 27" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
      </svg>
    ),
  },
];

function useCountUp(target, duration = 2200, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, index, inView }) {
  const count = useCountUp(stat.value, 2000 + index * 150, inView);

  return (
    <div
      className={`stat-card ${inView ? "stat-card--visible" : ""}`}
      style={{ "--delay": `${index * 0.12}s` }}
    >
      {/* Corner ornaments */}
      <span className="stat-card__corner stat-card__corner--tl" />
      <span className="stat-card__corner stat-card__corner--tr" />
      <span className="stat-card__corner stat-card__corner--bl" />
      <span className="stat-card__corner stat-card__corner--br" />

      {/* Shimmer overlay */}
      <div className="stat-card__shimmer" />

      {/* Icon */}
      <div className="stat-card__icon">{stat.icon}</div>

      {/* Number */}
      <div className="stat-card__number">
        <span className="stat-card__count">{count.toLocaleString()}</span>
        <span className="stat-card__suffix">{stat.suffix}</span>
      </div>

      {/* Divider */}
      <div className="stat-card__divider">
        <span className="stat-card__divider-gem" />
      </div>

      {/* Labels */}
      <div className="stat-card__label">{stat.label}</div>
      <div className="stat-card__sublabel">{stat.subLabel}</div>
    </div>
  );
}

export default function ImpactStats() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const { t, lang } = useLanguage();
  const impactData = t.impactStats;

  /* ── Structural Assembly combining Translation Dictionaries with Base Mappings ── */
  const localizedStats = useMemo(() => {
    const rawStatsCopy = impactData?.stats || {};
    return STATS_STRUCTURE.map((item) => {
      const copy = rawStatsCopy[item.id] || {};
      return {
        ...item,
        label: copy.label || '',
        subLabel: copy.subLabel || '',
      };
    });
  }, [impactData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={`impact-section ${lang === 'ta' ? 'impact-section--tamil' : ''}`} ref={sectionRef}>
      {/* Background noise texture layer */}
      <div className="impact-section__noise" />

      {/* Decorative gold arc top */}
      <div className="impact-section__arc impact-section__arc--top" />

      {/* Header */}
      <div className={`impact-header ${inView ? "impact-header--visible" : ""}`}>
        <p className="impact-header__eyebrow">
          <span className="impact-header__eyebrow-line" aria-hidden="true" />
          {impactData?.eyebrow}
          <span className="impact-header__eyebrow-line" aria-hidden="true" />
        </p>
        <h2 className="impact-header__title">{impactData?.title}</h2>
        <p className="impact-header__subtitle">{impactData?.subtitle}</p>
      </div>

      {/* Stats Grid */}
      <div className="impact-grid">
        {localizedStats.map((stat, i) => (
          <StatCard key={stat.id} stat={stat} index={i} inView={inView} />
        ))}
      </div>

      {/* Bottom ornament */}
      <div className="impact-section__bottom-ornament">
        <svg viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 10 L85 10" stroke="#F4C764" strokeWidth="0.6" strokeOpacity="0.5"/>
          <polygon points="100,2 107,10 100,18 93,10" fill="none" stroke="#F4C764" strokeWidth="0.8"/>
          <circle cx="100" cy="10" r="2" fill="#F4C764" fillOpacity="0.7"/>
          <path d="M115 10 L200 10" stroke="#F4C764" strokeWidth="0.6" strokeOpacity="0.5"/>
        </svg>
      </div>
    </section>
  );
}